// SubscribeButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Loading, { LoadingType } from '../component/Loading.jsx';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { ProductType } from '../resource/shopConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import ResetButtonStyle from '../style/ResetButtonStyle';
import { color, textColor } from '../style/variables.js';

import SubscribeIconSource from '../../img/ic_subscribe.svg';
import CheckIconSource from '../../img/ic_complete_white_xs.svg';

export class SubscribeButton extends React.PureComponent {
  getIsSubscribed = () => {
    const { userId, meId, timestampOffsetSeconds, meSubscription } = this.props;
    const { nbf, exp } = meSubscription;
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    return meId && (meId === userId || (now >= nbf && now < exp));
  };
  trackUserClick = ({ buttonId }) => {
    return trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
      },
    });
  };
  fetchDiamondPacks = ({ shouldRefetch = false } = {}) => {
    const {
      shopId,
      userId,
      defaultSubscriptionCurrency,
      isIntersected,
      isSubscriptionProductFetched,
      isSubscriptionProductFetching,
      fetchDiamondPacks,
    } = this.props;
    if (
      shouldRefetch ||
      (!isSubscriptionProductFetched && !isSubscriptionProductFetching)
    ) {
      if (userId && isIntersected) {
        return fetchDiamondPacks({
          shopId,
          currency: defaultSubscriptionCurrency,
          type: ProductType.SUBSCRIPTIONS,
          productIds: [userId],
        });
      }
    }
    return;
  };
  fetchUserSubscription = ({ shouldRefetch = false } = {}) => {
    const {
      userId,
      meId,
      isIntersected,
      isSubscriptionFetched,
      isSubscriptionFetching,
      fetchUserSubscription,
    } = this.props;
    if (
      shouldRefetch ||
      (meId &&
        userId &&
        isIntersected &&
        !isSubscriptionFetched &&
        !isSubscriptionFetching)
    ) {
      return fetchUserSubscription({
        userId: userId,
      });
    }
    return;
  };
  componentDidMount() {
    this.fetchDiamondPacks();
    this.fetchUserSubscription();
  }
  componentDidUpdate(prevProps) {
    const { meId, userId, defaultSubscriptionCurrency, isIntersected } =
      this.props;
    const isMeIdChanged = meId !== prevProps.meId;
    const isUserIdChanged = userId !== prevProps.userId;
    const isIsIntersectedChanged = isIntersected !== prevProps.isIntersected;
    const isDefaultSubscriptionCurrencyChanged =
      defaultSubscriptionCurrency !== prevProps.defaultSubscriptionCurrency;
    if (isUserIdChanged || isMeIdChanged || isIsIntersectedChanged) {
      this.fetchUserSubscription({
        shouldRefetch: isMeIdChanged ? true : false,
      });
    }
    if (
      isUserIdChanged ||
      isMeIdChanged ||
      isIsIntersectedChanged ||
      isDefaultSubscriptionCurrencyChanged
    ) {
      this.fetchDiamondPacks({ shouldRefetch: isMeIdChanged ? true : false });
    }
  }
  render() {
    const {
      meId,
      shopId,
      userId,
      defaultSubscriptionCurrency,
      unsubscribeI18nKey,
      isOnProfile,
      isSubscriptionProductFetched,
      subscriptionProductIds,
      t,
      toggleSubscribeAndUnlockModal,
      login,
    } = this.props;
    const isSubscribed = this.getIsSubscribed();
    if (!subscriptionProductIds.length && isSubscriptionProductFetched) {
      return null;
    }
    return (
      <StyledSubscribeButton
        isSubscribed={isSubscribed}
        isOnProfile={isOnProfile}
        disabled={isSubscribed || !isSubscriptionProductFetched}
        onClick={() => {
          if (!meId) {
            return login();
          }
          this.trackUserClick({
            buttonId: ButtonId.All.ButtonSubscribe,
          });
          return toggleSubscribeAndUnlockModal({
            userId,
            shopId,
            currency: defaultSubscriptionCurrency,
            isOpen: true,
          });
        }}
      >
        {!isSubscriptionProductFetched ? (
          <Loading type={LoadingType.WHITE} size={24} />
        ) : (
          <>
            <img src={isSubscribed ? CheckIconSource : SubscribeIconSource} />
            {t(isSubscribed ? 'subscribed' : unsubscribeI18nKey, {
              ns: TranslationNamespace.GENERAL,
            })}
          </>
        )}
      </StyledSubscribeButton>
    );
  }
}

SubscribeButton.propTypes = {
  userId: PropTypes.string,
  meId: PropTypes.string,
  shopId: PropTypes.string,
  defaultSubscriptionCurrency: PropTypes.string,
  unsubscribeI18nKey: PropTypes.string,
  timestampOffsetSeconds: PropTypes.number,
  isIntersected: PropTypes.bool,
  isSubscriptionFetched: PropTypes.bool,
  isSubscriptionFetching: PropTypes.bool,
  isSubscriptionProductFetched: PropTypes.bool,
  isSubscriptionProductFetching: PropTypes.bool,
  isOnProfile: PropTypes.bool,
  subscriptionProductIds: PropTypes.array,
  meSubscription: PropTypes.object,
  t: PropTypes.func.isRequired,
  fetchDiamondPacks: PropTypes.func,
  fetchUserSubscription: PropTypes.func,
  login: PropTypes.func,
  toggleSubscribeAndUnlockModal: PropTypes.func,
};

SubscribeButton.defaultProps = {
  userId: null,
  meId: null,
  shopId: null,
  defaultSubscriptionCurrency: null,
  unsubscribeI18nKey: 'subscribe',
  timestampOffsetSeconds: 0,
  isIntersected: false,
  isSubscriptionFetched: false,
  isSubscriptionFetching: false,
  isSubscriptionProductFetched: false,
  isSubscriptionProductFetching: false,
  isOnProfile: false,
  subscriptionProductIds: [],
  meSubscription: {},
  fetchDiamondPacks: () => null,
  fetchUserSubscription: () => null,
  login: () => null,
  toggleSubscribeAndUnlockModal: () => null,
};

const StyledSubscribeButton = styled.button.attrs(
  ({ isSubscribed, isOnProfile }) => ({
    style: {
      border: `1px solid ${isSubscribed ? color.grey['700'] : '#734eff'}`,
      height: `${isOnProfile ? 36 : 40}px`,
      backgroundColor: isSubscribed ? 'rgba(255, 255, 255, 0.1)' : '#734eff',
      color: isSubscribed ? 'rgba(255, 255, 255, 0.5)' : textColor.white,
      fontSize: `${isOnProfile ? 16 : 18}px`,
    },
  })
)`
  ${ResetButtonStyle};
  border-radius: 4px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  > img {
    margin-right: 4px;
    width: ${({ isOnProfile }) => (isOnProfile ? 20 : 24)}px;
    height: ${({ isOnProfile }) => (isOnProfile ? 20 : 24)}px;
    object-fit: contain;
    opacity: ${({ isSubscribed }) => (isSubscribed ? 0.5 : 1)};
  }
`;

export default withTranslation()(SubscribeButton);
